"use client";

import nowadaysIcon from "@/app/(public)/shared/assets/nowadays-icon.png";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { getNavbarIcon } from "@/app/(public)/shared/utils/navbarIcons";
import { createClient } from "@/utils/supabase/client";
import { Menu, NavArrowDown } from "iconoir-react";
import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Dropdown content data
const dropdownContent = {
  Products: {
    sections: [
      {
        title: "Planning Tools",
        items: [
          { name: "Venue Sourcing", description: "Find perfect venues", href: "/venue-sourcing" },
          { name: "Registration", description: "Attendee registration", href: "/registration" },
          { name: "Budget Estimator", description: "Track event budgets", href: "/budget-estimator" },
          { name: "Flight Estimator", description: "Estimate travel costs", href: "/flight-estimator" },
          { name: "The Venue Collective", description: "Exclusive venue review community", href: "/community" },
          { name: "Create Event", description: "Create RFPs for venues", href: "/create" },
          { name: "Explore Venues", description: "Find the perfect venue", href: "/explore" },
          { name: "Reporting & Analytics", description: "Track event metrics", href: "/reporting" },
          { name: "Custom Event Sites", description: "Branded event pages", href: "/site-pages" },
          { name: "Hotel Room Blocks", description: "Block rooms for your event", href: "/hotel-room-blocks" },
          { name: "Marketing Activations", description: "Engaging your clients", href: "/activations" },
          { name: "Games", description: "Nowadays Games", href: "/games" },
        ],
      },
    ],
  },
  Resources: {
    sections: [
      {
        title: "Learn & Grow",
        items: [
          { name: "Case Studies", description: "Client success stories", href: "/case-studies" },
          { name: "Blogs", description: "Planning tips", href: "/blogs" },
          { name: "Comparisons", description: "Compare alternatives", href: "/comparisons" },
          { name: "Templates", description: "Ready-to-use templates", href: "/templates" },
        ],
      },
      {
        title: "Support & Community",
        items: [
          { name: "Support", description: "Get help", href: "/support" },
          { name: "Community", description: "Connect with planners", href: "/community" },
          { name: "Change Log", description: "Product updates", href: "/changelog" },
          { name: "Newsletter", description: "Stay updated", href: "/newsletter" },
          { name: "FAQs", description: "Frequently asked questions", href: "/faq" },
          { name: "Glossary", description: "Definitions of terms", href: "/glossary" },
        ],
      },
    ],
  },
  Company: {
    sections: [
      {
        title: "About Us",
        items: [
          { name: "About Nowadays", description: "Our story", href: "/company" },
          { name: "Newsroom", description: "Company news", href: "/press" },
          { name: "Team", description: "Meet the team", href: "/team" },
          { name: "Careers", description: "Join our team", href: "/careers" },
        ],
      },
      {
        title: "Get Started",
        items: [
          { name: "Pricing", description: "Plans and pricing", href: "/pricing" },
          { name: "Partner with us", description: "Become a partner", href: "/partner" },
          { name: "Referral Program", description: "Earn rewards", href: "/referral" },
          {
            name: "Planner Profiles",
            description: "Global Account Manager profiles",
            href: "/global-account-managers",
          },
        ],
      },
    ],
  },
  Solutions: {
    sections: [
      {
        title: "By Company Size",
        items: [
          { name: "For Startups", description: "Growing companies", href: "/solutions/startups" },
          { name: "For Enterprise", description: "Enterprise-grade events", href: "/solutions/enterprise" },
        ],
      },
      {
        title: "By Role",
        items: [
          { name: "For People Teams", description: "HR and team building", href: "/people-teams" },
          {
            name: "For Executive Assistants",
            description: "Executive coordination",
            href: "/executive-assistants",
          },
          {
            name: "For Event Marketers",
            description: "Marketing events",
            href: "/event-marketers",
          },
          { name: "For Agencies", description: "Agency solutions", href: "/agencies" },
          { name: "For Travel Agents", description: "Travel coordination", href: "/travel-agents" },
          { name: "For Hoteliers & Venues", description: "Venue management", href: "/partners" },
        ],
      },
    ],
  },
};

export const Navbar = () => {
  const supabase = createClient();
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [prevDropdown, setPrevDropdown] = useState<string | null>(null);
  const [isHoveringNav, setIsHoveringNav] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [direction, setDirection] = useState<"left" | "right" | null>(null);
  const [animatingOut, setAnimatingOut] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, width: 0 });
  const [session, setSession] = useState<any>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [clickedItemId, setClickedItemId] = useState<string | null>(null);

  // Mobile state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileAccordions, setOpenMobileAccordions] = useState<string[]>([]);

  const animatingOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileNavBarRef = useRef<HTMLDivElement>(null);
  const desktopNavBarRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Handle navigation loading state
  const handleNavigationClick = (itemId: string) => {
    setClickedItemId(itemId);
    // Reset after a short delay to show the loading effect
    setTimeout(() => {
      setClickedItemId(null);
    }, 1000);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 15;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (animatingOutTimeoutRef.current) {
        clearTimeout(animatingOutTimeoutRef.current);
      }
    };
  }, []);

  // Check authentication state
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setSession(session);
      } catch (error) {
        console.error("Error checking auth:", error);
        setSession(null);
      } finally {
        setIsLoadingAuth(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setIsLoadingAuth(false);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  /**
   * Publishes the visible public nav bar height (mobile or desktop) so the
   * mobile drawer top/maxHeight and landing hero padding stay aligned.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const mobile = mobileNavBarRef.current;
    const desktop = desktopNavBarRef.current;

    const sync = () => {
      const el = window.innerWidth >= 1024 ? desktop : mobile;
      if (!el) {
        root.style.removeProperty("--mobile-nav-height");
        return;
      }
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        root.style.setProperty("--mobile-nav-height", `${Math.round(h * 100) / 100}px`);
      } else {
        root.style.removeProperty("--mobile-nav-height");
      }
    };

    sync();
    const ro = new ResizeObserver(sync);
    if (mobile) ro.observe(mobile);
    if (desktop) ro.observe(desktop);
    window.addEventListener("resize", sync);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
      root.style.removeProperty("--mobile-nav-height");
    };
  }, [scrolled, session, isLoadingAuth, activeDropdown]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobileMenuOpen &&
        !(event.target as Element).closest(".mobile-menu-container") &&
        !(event.target as Element).closest(".mobile-menu-button")
      ) {
        setIsMobileMenuOpen(false);
        setOpenMobileAccordions([]);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Mobile accordion toggle
  const toggleMobileAccordion = (accordionKey: string) => {
    setOpenMobileAccordions((prev) => {
      // If the clicked accordion is already open, close it
      if (prev.includes(accordionKey)) {
        return prev.filter((key) => key !== accordionKey);
      }
      // If opening a new accordion, close all others and open only this one
      return [accordionKey];
    });
  };

  // Mobile menu toggle with proper state reset
  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      // Close menu and reset accordions
      setIsMobileMenuOpen(false);
      setOpenMobileAccordions([]);
    } else {
      // Open menu
      setIsMobileMenuOpen(true);
    }
  };

  const menuItems = Object.keys(dropdownContent);

  const resetDropdownState = (newActiveItem?: string) => {
    if (animatingOutTimeoutRef.current) {
      clearTimeout(animatingOutTimeoutRef.current);
      animatingOutTimeoutRef.current = null;
    }
    setAnimatingOut(false);
    if (newActiveItem !== undefined) {
      setActiveDropdown(newActiveItem);
      setShowDropdown(!!newActiveItem);
    } else {
      setActiveDropdown(null);
      setShowDropdown(false);
      setPrevDropdown(null);
      setDirection(null);
    }
  };

  const handleNavMouseEnter = () => {
    setIsHoveringNav(true);
    if (animatingOutTimeoutRef.current) {
      clearTimeout(animatingOutTimeoutRef.current);
      animatingOutTimeoutRef.current = null;
      setAnimatingOut(false);
    }
  };

  const handleNavMouseLeave = () => {
    setIsHoveringNav(false);
    setAnimatingOut(true);
    animatingOutTimeoutRef.current = setTimeout(() => {
      resetDropdownState();
    }, 300);
  };

  const handleDropdownMouseEnter = (item: string) => {
    if (animatingOutTimeoutRef.current) {
      clearTimeout(animatingOutTimeoutRef.current);
      animatingOutTimeoutRef.current = null;
      setAnimatingOut(false);
    }

    if (activeDropdown === item) return;

    // Calculate direction for Stripe-like morphing
    const currentIndex = menuItems.indexOf(item);
    const prevIndex = activeDropdown ? menuItems.indexOf(activeDropdown) : -1;

    if (prevIndex !== -1 && currentIndex !== -1) {
      setDirection(currentIndex > prevIndex ? "right" : "left");
      setPrevDropdown(activeDropdown);
    }

    // Calculate dropdown position based on hovered nav item
    const navItem = navItemRefs.current[item];
    if (navItem) {
      const rect = navItem.getBoundingClientRect();
      const navContainer = navItem.closest(".flex.justify-start.items-center.gap-4");
      if (navContainer) {
        const containerRect = navContainer.getBoundingClientRect();
        const relativeX = rect.left - containerRect.left + rect.width / 2;
        setDropdownPosition({ x: relativeX, width: rect.width });
      }
    } else {
      // Fallback to center if ref not found
      setDropdownPosition({ x: 200, width: 100 });
    }

    setActiveDropdown(item);
    setIsHoveringNav(true);
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  const handleDropdownMouseLeave = () => {
    setIsHoveringNav(false);
    setAnimatingOut(true);
    animatingOutTimeoutRef.current = setTimeout(() => {
      if (!isHoveringNav) {
        resetDropdownState();
      }
    }, 300);
  };

  // Animation variants for Stripe-like interactions
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.15 },
    },
  };

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -8,
      scale: 0.96,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    exit: {
      opacity: 0,
      y: -8,
      scale: 0.96,
    },
  };

  const contentVariants = {
    hidden: {
      opacity: 0,
      x: direction === "right" ? 20 : direction === "left" ? -20 : 0,
    },
    visible: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: direction === "right" ? -20 : direction === "left" ? 20 : 0,
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.02,
        duration: 0.3,
      },
    }),
  };

  return (
    <>
      {/* Background overlay when dropdown is active */}
      <AnimatePresence>
        {activeDropdown && (
          <motion.div
            className="fixed inset-0 z-40"
            style={{
              background: `linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.3))`,
            }}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseEnter={handleDropdownMouseLeave}
          />
        )}
      </AnimatePresence>

      {/* Navbar dropdown background overlay - on top of gradient */}
      <AnimatePresence>
        {activeDropdown && (
          <motion.div
            className="fixed inset-0 z-50"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseEnter={handleDropdownMouseLeave}
          >
            {/* <img src={navbarDropdownBg.src} alt="" className="w-full h-full object-cover" /> */}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Navbar — height is measured into --mobile-nav-height for the drawer */}
      <div
        ref={mobileNavBarRef}
        className={`lg:hidden box-border flex w-full items-center justify-center fixed z-50 bg-landing-primary py-0 transition-all duration-300 ease-in-out ${
          scrolled ? "border-b border-landing-primary" : ""
        }`}
        style={{
          top: "var(--promo-offset, 0px)",
        }}
      >
        <div className="flex h-full w-full max-w-7xl items-center justify-between px-6 py-2">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-1 md:gap-2" onClick={() => handleNavigationClick("/")}>
            <img
              className="h-[36px] w-[36px] md:h-[42px] md:w-[42px] object-contain flex-shrink-0"
              src={nowadaysIcon.src}
              alt="Nowadays Icon"
            />
            <div
              className="text-logo whitespace-nowrap transition-all duration-[600ms] ease-out text-landing-primary"
              style={{
                opacity: scrolled ? 0 : 1,
                width: scrolled ? "0px" : "120px",
              }}
            >
              nowadays
            </div>
          </Link>

          {/* Right side buttons */}
          <div className="flex items-center gap-2">
            {/* Auth Button */}
            {!isLoadingAuth && (
              <>
                {session ? (
                  <Button
                    variant="ghost"
                    color="neutral"
                    size="sm"
                    onClick={() => {
                      handleNavigationClick("/dashboard");
                      window.location.href = "/dashboard";
                    }}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    color="neutral"
                    size="sm"
                    onClick={() => {
                      handleNavigationClick("/login");
                      window.location.href = "/login";
                    }}
                  >
                    Log In
                  </Button>
                )}
              </>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="mobile-menu-button p-2 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors text-gray-700 hover:text-gray-900"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 mobile-menu-container overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              backgroundColor: "var(--landing-bg-primary-purple)",
              top: "calc(var(--promo-offset, 0px) + var(--mobile-nav-height, 3.5rem))",
              maxHeight: "calc(100vh - var(--promo-offset, 0px) - var(--mobile-nav-height, 3.5rem))",
            }}
          >
            <div className="max-w-7xl mx-auto px-6 py-6 space-y-6 min-h-full">
              {/* Products Accordion */}
              <div className="border-b border-landing-primary pb-6">
                <button
                  onClick={() => toggleMobileAccordion("products")}
                  className="w-full flex justify-between items-center text-left text-landing-primary"
                >
                  <span className="text-body-lg font-medium">Products</span>
                  <motion.div
                    animate={{ rotate: openMobileAccordions.includes("products") ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <NavArrowDown />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openMobileAccordions.includes("products") && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-4">
                        {dropdownContent.Products.sections.map((section: any, sectionIndex: number) => (
                          <div key={sectionIndex}>
                            <h4 className="text-body-sm font-medium text-landing-secondary mb-3">{section.title}</h4>
                            {/* Mobile: Single column, Tablet: 2-column grid */}
                            <div className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                              {section.items.map((item: any, itemIndex: number) => {
                                const IconComponent = getNavbarIcon("Products", section.title, item.name);
                                return (
                                  <Link
                                    key={itemIndex}
                                    href={item.href}
                                    className="flex items-center gap-3 py-2 rounded-lg hover:bg-landing-primary/5 transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-landing-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                                      {IconComponent && (
                                        <IconComponent
                                          className="w-5 h-5 md:w-6 md:h-6 text-landing-secondary"
                                          strokeWidth={1.75}
                                        />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-body-sm font-medium text-landing-primary">{item.name}</div>
                                      <div className="text-body-sm text-landing-secondary mt-1">{item.description}</div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Resources Accordion */}
              <div className="border-b border-landing-primary pb-6">
                <button
                  onClick={() => toggleMobileAccordion("resources")}
                  className="w-full flex justify-between items-center text-left text-landing-primary"
                >
                  <span className="text-body-lg font-medium">Resources</span>
                  <motion.div
                    animate={{ rotate: openMobileAccordions.includes("resources") ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <NavArrowDown />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openMobileAccordions.includes("resources") && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-4">
                        {dropdownContent.Resources.sections.map((section: any, sectionIndex: number) => (
                          <div key={sectionIndex}>
                            <h4 className="text-body-sm font-medium text-landing-secondary mb-3">{section.title}</h4>
                            {/* Mobile: Single column, Tablet: 2-column grid */}
                            <div className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                              {section.items.map((item: any, itemIndex: number) => {
                                const IconComponent = getNavbarIcon("Resources", section.title, item.name);
                                return (
                                  <Link
                                    key={itemIndex}
                                    href={item.href}
                                    className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-landing-primary/5 transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-landing-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                                      {IconComponent && (
                                        <IconComponent
                                          className="w-5 h-5 md:w-6 md:h-6 text-landing-secondary"
                                          strokeWidth={1.75}
                                        />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-body-sm font-medium text-landing-primary">{item.name}</div>
                                      <div className="text-body-sm text-landing-secondary mt-1">{item.description}</div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Company Accordion */}
              <div className="border-b border-landing-primary pb-6">
                <button
                  onClick={() => toggleMobileAccordion("company")}
                  className="w-full flex justify-between items-center text-left text-landing-primary"
                >
                  <span className="text-body-lg font-medium">Company</span>
                  <motion.div
                    animate={{ rotate: openMobileAccordions.includes("company") ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <NavArrowDown />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openMobileAccordions.includes("company") && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-4">
                        {dropdownContent.Company.sections.map((section: any, sectionIndex: number) => (
                          <div key={sectionIndex}>
                            <h4 className="text-body-sm font-medium text-landing-secondary mb-3">{section.title}</h4>
                            {/* Mobile: Single column, Tablet: 2-column grid */}
                            <div className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                              {section.items.map((item: any, itemIndex: number) => {
                                const IconComponent = getNavbarIcon("Company", section.title, item.name);
                                return (
                                  <Link
                                    key={itemIndex}
                                    href={item.href}
                                    className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-landing-primary/5 transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-landing-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                                      {IconComponent && (
                                        <IconComponent
                                          className="w-5 h-5 md:w-6 md:h-6 text-landing-secondary"
                                          strokeWidth={1.75}
                                        />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-body-sm font-medium text-landing-primary">{item.name}</div>
                                      <div className="text-body-sm text-landing-secondary mt-1">{item.description}</div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Solutions Accordion */}
              <div className="border-b border-landing-primary pb-6">
                <button
                  onClick={() => toggleMobileAccordion("solutions")}
                  className="w-full flex justify-between items-center text-left text-landing-primary"
                >
                  <span className="text-body-lg font-medium">Solutions</span>
                  <motion.div
                    animate={{ rotate: openMobileAccordions.includes("solutions") ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <NavArrowDown />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openMobileAccordions.includes("solutions") && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-4">
                        {dropdownContent.Solutions.sections.map((section: any, sectionIndex: number) => (
                          <div key={sectionIndex}>
                            <h4 className="text-body-sm font-medium text-landing-secondary mb-3">{section.title}</h4>
                            {/* Mobile: Single column, Tablet: 2-column grid */}
                            <div className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                              {section.items.map((item: any, itemIndex: number) => {
                                const IconComponent = getNavbarIcon("Solutions", section.title, item.name);
                                return (
                                  <Link
                                    key={itemIndex}
                                    href={item.href}
                                    className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-landing-primary/5 transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-landing-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                                      {IconComponent && (
                                        <IconComponent
                                          className="w-5 h-5 md:w-6 md:h-6 text-landing-secondary"
                                          strokeWidth={1.75}
                                        />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-body-sm font-medium text-landing-primary">{item.name}</div>
                                      <div className="text-body-sm text-landing-secondary mt-1">{item.description}</div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Try Our Platform Button */}
              <div className="pt-4">
                <Link href="/create" className="block">
                  <Button variant="primary" color="primary" className="w-full">
                    Try our platform
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Navbar */}
      <div
        ref={desktopNavBarRef}
        className={`hidden lg:flex w-full justify-center items-center fixed top-[var(--promo-offset,0px)] transition-all duration-300 ease-in-out py-2.5 ${
          scrolled ? "bg-landing-primary border-b border-landing-primary" : "bg-transparent"
        }`}
        style={{
          zIndex: activeDropdown ? 70 : 50,
        }}
      >
        <div
          className={`container-page flex justify-between items-center transition-all duration-300 ${
            scrolled ? "scale-100" : "scale-100"
          }`}
        >
          {/* Left side - Logo and Navigation */}
          <div className="flex justify-start items-center gap-6">
            {/* Logo */}
            <div
              className="flex justify-start items-start gap-2 cursor-pointer"
              onClick={() => {
                handleNavigationClick("/");
                window.location.href = "/";
              }}
            >
              <img
                className="h-[40px] w-[32px] object-contain flex-shrink-0"
                src={nowadaysIcon.src}
                alt="Nowadays Icon"
              />
              <div
                className="text-logo whitespace-nowrap transition-all duration-[600ms] ease-out text-landing-primary"
                style={{
                  opacity: scrolled ? 0 : 1,
                  width: scrolled ? "0px" : "120px",
                }}
              >
                nowadays
              </div>
            </div>

            {/* Divider */}
            <div className="w-[2px] rounded-full h-4 transition-colors duration-300 bg-border-landing-primary" />

            {/* Navigation Links */}
            <div
              className="flex justify-start items-center gap-4 relative"
              onMouseEnter={handleNavMouseEnter}
              onMouseLeave={handleNavMouseLeave}
            >
              {Object.keys(dropdownContent).map((item) => (
                <div
                  key={item}
                  className="relative"
                  ref={(el) => {
                    navItemRefs.current[item] = el;
                  }}
                  onMouseEnter={() => handleDropdownMouseEnter(item)}
                >
                  <motion.div className="text-nav-button cursor-pointer relative px-3 py-2 rounded-md transition-all duration-200 text-landing-primary">
                    {/* Background hover state */}
                    <motion.div
                      className="absolute inset-0 rounded-md"
                      style={{
                        background: "rgba(255, 255, 255, 1)",
                      }}
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: activeDropdown === item ? 1 : 0,
                      }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                    <span className="relative z-10">{item}</span>
                  </motion.div>
                </div>
              ))}

              {/* Stripe-like morphing dropdown container */}
              <AnimatePresence>
                {showDropdown && (
                  <motion.div
                    className="absolute top-full pt-4 z-50"
                    style={{
                      left: `${dropdownPosition.x - 100}px`,
                      transform: "translateX(-50%)",
                    }}
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onMouseEnter={() => setIsHoveringNav(true)}
                    onMouseLeave={handleDropdownMouseLeave}
                    transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    layout
                  >
                    <motion.div
                      className="bg-white rounded-2xl shadow-2xl border border-landing-primary overflow-hidden w-fit"
                      layout
                      transition={{
                        layout: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
                      }}
                      ref={dropdownRef}
                    >
                      <AnimatePresence mode="wait">
                        {activeDropdown && (
                          <motion.div
                            key={activeDropdown}
                            variants={contentVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            transition={{
                              duration: 0.2,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                          >
                            <div className="pt-8 px-8 pb-10 bg-white rounded-2xl">
                              {/* Main sections */}
                              <div className="inline-flex flex-col justify-start items-start gap-10">
                                {dropdownContent[activeDropdown as keyof typeof dropdownContent].sections.map(
                                  (section, sectionIndex) => (
                                    <div key={sectionIndex} className="flex flex-col justify-start items-start gap-6">
                                      <div className="self-stretch justify-start text-body-lg font-medium text-landing-secondary">
                                        {section.title}
                                      </div>
                                      <div className="self-stretch flex flex-col gap-4">
                                        {Array.from({ length: Math.ceil(section.items.length / 2) }, (_, rowIndex) => (
                                          <div key={rowIndex} className="inline-flex justify-start items-center gap-16">
                                            {section.items
                                              .slice(rowIndex * 2, (rowIndex + 1) * 2)
                                              .map((subItem, itemIndex) => {
                                                const IconComponent = getNavbarIcon(
                                                  activeDropdown,
                                                  section.title,
                                                  subItem.name
                                                );
                                                return (
                                                  <Link
                                                    key={rowIndex * 3 + itemIndex}
                                                    href={subItem.href}
                                                    onClick={() => handleNavigationClick(subItem.href)}
                                                  >
                                                    <motion.div
                                                      className="w-60 max-w-60 flex justify-start items-center gap-3.5 cursor-pointer group"
                                                      initial="hidden"
                                                      animate="visible"
                                                      custom={sectionIndex * 2 + rowIndex * 2 + itemIndex}
                                                      variants={itemVariants}
                                                    >
                                                      <div
                                                        className={`w-12 h-12 relative rounded-lg flex items-center justify-center transition-all duration-200 ${
                                                          clickedItemId === subItem.href
                                                            ? "bg-landing-secondary"
                                                            : "bg-landing-secondary group-hover:bg-indigo-400"
                                                        }`}
                                                      >
                                                        {IconComponent && (
                                                          <IconComponent
                                                            className={`w-6 h-6 transition-all duration-200 ${
                                                              clickedItemId === subItem.href
                                                                ? "text-indigo-400"
                                                                : "text-landing-secondary group-hover:text-white"
                                                            }`}
                                                            strokeWidth={1.75}
                                                          />
                                                        )}
                                                      </div>
                                                      <div className="flex-1 inline-flex flex-col justify-start items-start">
                                                        <div className="self-stretch justify-start text-body-sm font-medium text-landing-primary">
                                                          {subItem.name}
                                                        </div>
                                                        {subItem.description && (
                                                          <div className="self-stretch justify-start text-body-sm font-medium text-landing-secondary">
                                                            {subItem.description}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </motion.div>
                                                  </Link>
                                                );
                                              })}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right side - Buttons */}
          <div className="flex justify-start items-center gap-2">
            {!isLoadingAuth && (
              <>
                {session ? (
                  <Button
                    variant="ghost"
                    color="primary"
                    size="sm"
                    onClick={() => {
                      handleNavigationClick("/dashboard");
                      window.location.href = "/dashboard";
                    }}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    color="primary"
                    size="sm"
                    onClick={() => {
                      handleNavigationClick("/login");
                      window.location.href = "/login";
                    }}
                  >
                    Log In
                  </Button>
                )}
                <Link href="/create">
                  <Button variant={scrolled ? "primary" : "outline"} color="primary" size="sm">
                    Try our platform
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
